import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import AppointmentNoteActions from "./notes/AppointmentNoteActions";
import NoteCard from "./notes/NoteCard";
import NoteComposer from "./notes/NoteComposer";
import AssessmentCard from "./customForms/AssessmentCard";
import AssessmentComposer from "./customForms/AssessmentComposer";
import { buildClinicalItems, splitClinicalItems } from "./notes/clinicalItems";
import { useNoteActions } from "./notes/useNoteActions";
import { syncService } from "../services/SyncService";
import { useUserRole } from "../hooks/useUserRole";
import { Button } from "./ui/button";

export default function AppointmentNotes({ appointmentId, userId, token }) {
  const { getRoleForClinic } = useUserRole();
  const [showNotes, setShowNotes] = useState(false);
  const [activeMode, setActiveMode] = useState('none'); // 'none' | 'composer' | 'assessment'
  const [correctionAssessment, setCorrectionAssessment] = useState(null);
  const [showEnteredInError, setShowEnteredInError] = useState(false);

  const notes = useLiveQuery(
    () => db.appointment_notes.where("appointment_id").equals(appointmentId).toArray(),
    [appointmentId]
  ) || [];
  const assessments = useLiveQuery(
    () => db.form_assessments.where("appointment_id").equals(appointmentId).toArray(),
    [appointmentId]
  ) || [];
  const assessmentIds = useMemo(() => assessments.map(item => item.id), [assessments]);
  const revisions = useLiveQuery(
    () => (assessmentIds.length ? db.form_assessment_revisions.where("assessment_id").anyOf(assessmentIds).toArray() : []),
    [assessmentIds.join("|")]
  ) || [];
  const templates = useLiveQuery(() => db.form_templates.toArray(), []) || [];
  const versions = useLiveQuery(() => db.form_template_versions.toArray(), []) || [];
  const users = useLiveQuery(() => db.users.toArray(), []) || [];

  const actions = useNoteActions({ userId, token });

  const clinicalItems = useMemo(
    () => buildClinicalItems({ notes, assessments, revisions, templates, versions, users }),
    [notes, assessments, revisions, templates, versions, users]
  );

  const { defaultItems, enteredInErrorItems } = useMemo(
    () => splitClinicalItems(clinicalItems),
    [clinicalItems]
  );

  const visibleItems = useMemo(
    () => (showEnteredInError ? clinicalItems : defaultItems),
    [showEnteredInError, clinicalItems, defaultItems]
  );

  const reversedItems = useMemo(() => [...visibleItems].reverse(), [visibleItems]);

  const handleEnteredInError = async (assessment, reason) => {
    const queueRecord = {
      type: 'create',
      entity: 'form_assessment_entered_in_error',
      entity_id: assessment.id,
      data: { assessment_id: assessment.id, reason },
      status: 'pending',
      created_at: new Date().toISOString(),
    };
    await db.transaction('rw', db.form_assessments, db.sync_queue, async () => {
      await db.form_assessments.update(assessment.id, {
        status: 'entered_in_error',
        entered_in_error_reason: reason,
        entered_in_error_by: userId,
        entered_in_error_at: new Date().toISOString(),
        sync_status: 'pending',
        _synced: false,
      });
      await db.sync_queue.add(queueRecord);
    });
    syncService.processQueue();
  };

  const handleCorrect = (assessment, revision) => {
    setCorrectionAssessment({ ...assessment, answers: revision.answers });
    setActiveMode('assessment');
  };

  return (
    <div className="mt-4 border-t pt-2">
      <button type="button" onClick={() => setShowNotes(value => !value)} className="flex items-center gap-2 py-2 text-sm font-semibold text-slate-600">
        {showNotes ? <ChevronUp size={16} /> : <ChevronDown size={16} />} Notes ({clinicalItems.length})
      </button>
      {showNotes && (
        <div className="mt-2 space-y-4">
          {activeMode === 'assessment' ? (
            <AssessmentComposer
              appointmentId={appointmentId}
              userId={userId}
              correctionAssessment={correctionAssessment}
              onClose={() => { setActiveMode('none'); setCorrectionAssessment(null); }}
              onSubmitted={() => { setActiveMode('none'); setCorrectionAssessment(null); }}
            />
          ) : (
            <NoteComposer
              appointmentId={appointmentId}
              actions={actions}
              onStartAssessment={() => setActiveMode('assessment')}
            />
          )}

          <AppointmentNoteActions appointmentId={appointmentId} notes={notes} hasClinicalContent={clinicalItems.length > 0} token={token} />

          {enteredInErrorItems.length > 0 && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => setShowEnteredInError(v => !v)}>
                {showEnteredInError ? 'Hide' : 'Show'} entries marked in error ({enteredInErrorItems.length})
              </Button>
            </div>
          )}

          <div className="max-h-[300px] space-y-3 overflow-y-auto pr-1">
            {reversedItems.map(item => {
              if (item.kind === 'assessment') {
                return <AssessmentCard
                  key={item.id}
                  assessment={item}
                  template={item.template}
                  version={item.version}
                  latestRevision={item.latestRevision}
                  revisionHistory={item.revisionHistory}
                  userId={userId}
                  canAdminister={getRoleForClinic(item.clinic_id) === 'admin'}
                  onCorrect={handleCorrect}
                  onEnteredInError={handleEnteredInError}
                />;
              }
              return <NoteCard
                key={item.id} note={item} userId={userId} token={token}
                onDelete={actions.deleteNote} onRetry={actions.retryMediaUpload}
                isUploading={actions.uploadingNoteId === item.id}
              />;
            })}
            {visibleItems.length === 0 && <p className="py-4 text-center text-xs italic text-slate-400">No notes for this appointment</p>}
          </div>
        </div>
      )}
    </div>
  );
}

AppointmentNotes.propTypes = {
  appointmentId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  token: PropTypes.string,
};
